import { useState } from 'react';

import styled from '@emotion/styled';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@repo/videa-components/src/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/videa-components/src/ui/select';
import { Switch } from '@repo/videa-components/src/ui/switch';
import { useAtom } from 'jotai';
import { useSelector } from 'react-redux';

import { isDirectorUser } from '@/modules/login/login-utils';

import type {
  PreferenceUpdateBody,
  PreferencesResponse,
} from '../../../@types/preferences';
import { VideaTypography } from '../../../common-components/elements/VideaTypography/VideaTypography';
import { ConfidenceDialog } from '../../../modules/annotator/displayThreshold/ConfidenceDialog';
import { RBLColorSettings } from '../../../modules/annotator/findingSidebar/findingList/rblColorSettings/RBLColorSettings';
import { useShouldHideHuddle } from '../../../modules/patientList/useShouldHideHuddle';
import {
  type TaskType,
  TaskTypes,
  useGetTaskDisplayName,
} from '../../../react-query/useGetTasks';
import {
  usePreferences,
  usePreferencesPracticeUpdate,
} from '../../../react-query/usePreferences';
import {
  blueBlack400,
  greyLight300,
  severitySevere,
  white,
} from '../../../theme/videa-style-tokens';
import { usePrlDevtoolsShortcut } from '../../../utils/hooks/useDevtoolsShortcut';
import { isPreferenceMenuOpen } from '../atoms';

import { FlourideTaskAgeInput } from './FlourideTaskAgeInput';
import { HQOffByDefault } from './HQOffByDefault';
import { RecommendationPreference } from './indicatorPreferences/IndicatorPreferences';
import { PatientEducationTxCopyDialog } from './PatientEducationTxCopyDialog';
import { PracticePreferencesSwitch } from './PracticePreferencesSwitch';
import { PreferenceScrollToCurrent } from './PreferenceScrollToCurrent';
import { PreferencesCustomizer } from './preferencesCustomizer';
import { SegmentationPreferenceSettingsToggle } from './SegmentationPreferenceSettingsToggle';
import { SpotlightPreference } from './SpotlightPreference';

const PrefPaneTitle = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  font-size: 1.125em;
  margin: 0.75em 0 0.5em 0;
`;

const PrefPane = styled.div`
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 0.5em;
  align-items: center;
  justify-content: flex-start;
  padding: 0.5em;
  background-color: ${greyLight300};
  border-radius: 0.5em;

  .pane-section {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    gap: 0.5em;

    .MuiTypography-root {
      display: flex;
      align-items: center;
    }
  }

  .pref-label {
    min-width: 5em;
  }

  .pane-section-column {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    gap: 0.5em;
    border-radius: 0.25em;
    padding: 0.5em 0.5em;
    margin-bottom: 0.5em;
  }

  .pref-control {
    .MuiInputBase-root {
      margin: 0 0.375em;
      box-sizing: border-box;
    }

    .MuiSelect-select {
      width: 100%;
      box-sizing: border-box;
      padding: 0.5em 0.75em;
      background-color: ${white};
    }
  }

  .pref-helper {
    display: inline-block;
    font-size: 0.875em;
    color: ${blueBlack400};
    padding: 0 1em 0 2em;
  }

  .pref-button-set {
    padding: 0;
    .button-reset {
      background-color: ${white};
      height: 3.5em;
      margin: 0 1em 0 0.25em;
    }
  }
  .pref-button {
    background-color: ${white};
    height: 3em;
  }

  .confidence-settings-button {
    svg {
      font-size: 2em;
    }
  }

  .pref-control-group {
    .MuiFormControlLabel-label {
      font-size: 1.125em;
    }
  }
`;

const EnableHqOnThumbnails = () => {
  const preferences = usePreferences();
  const preferencesUpdate = usePreferencesPracticeUpdate();
  const shouldEnableHqOnThumbnails = preferences?.data?.enableHqOnThumbnails;
  const { t } = useLingui();

  return (
    <>
      <Switch
        checked={shouldEnableHqOnThumbnails}
        onCheckedChange={(checked) =>
          preferencesUpdate.mutate({
            enableHqOnThumbnails: checked,
          })
        }
      />

      <VideaTypography className="pref-label" variant="labelSmall">
        <Trans>
          Image Quality (HQ) in FMX view
          <span className="pref-helper">
            {shouldEnableHqOnThumbnails
              ? t`Radiographs displayed in high quality in full FMX view`
              : t`Radiographs displayed in normal quality in full FMX view`}
          </span>
        </Trans>
      </VideaTypography>
    </>
  );
};

export const PracticePreferencesMenu = () => {
  // selectors
  const user = useSelector((state: any) => state.login.user);

  // hooks
  const { data: preferences } = usePreferences();
  const { showPrlDevtools } = usePrlDevtoolsShortcut();
  const preferencesUpdateMutation = usePreferencesPracticeUpdate();
  const hideHuddle = useShouldHideHuddle();
  const getTaskDisplayName = useGetTaskDisplayName();
  const { t } = useLingui();

  // state
  const [isOpen, setIsOpen] = useAtom(isPreferenceMenuOpen);
  const [selectedTask, selectedTaskSet] = useState<TaskType>('BW_DUE');

  // variables

  const isHQOffByDefault = preferences?.hqOffByDefault;
  const hidePracticePreferences = preferences?.disablePracticePreferences;
  const isDirector = isDirectorUser(user);

  // handlers
  const handleUpdatePreferences = (update: PreferenceUpdateBody) => {
    preferencesUpdateMutation.mutate(update);
  };

  const huddlePrefs = (
    <div className="pane-section-column">
      <legend
        style={{
          width: '100%',
          alignContent: 'center',
        }}
      >
        <p className="vai-typography-small !vai-mb-2">
          <Trans>Task Configuration</Trans>
        </p>
        <Select
          value={selectedTask}
          onValueChange={(value: TaskType) => selectedTaskSet(value)}
        >
          <SelectTrigger>
            <SelectValue placeholder={t`Task Configuration`} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={TaskTypes.BW_DUE}>
              {getTaskDisplayName('BW_DUE')} Task
            </SelectItem>
            <SelectItem value={TaskTypes.PERIO_CHART_DUE}>
              <Trans>Perio Chart Due Task</Trans>
            </SelectItem>
            <SelectItem value={TaskTypes.PROPHY_DUE}>
              {getTaskDisplayName('PROPHY_DUE')} Task
            </SelectItem>
            <SelectItem value={TaskTypes.PERIO_MAINTENANCE_DUE}>
              <Trans>Perio Maintenance Due Task</Trans>
            </SelectItem>
            <SelectItem value={TaskTypes.FLUORIDE_DUE}>
              {getTaskDisplayName('FLUORIDE_DUE')} Task
            </SelectItem>
            {preferences?.enableScanOpportunityTask && (
              <SelectItem value={TaskTypes.SCAN_OPPORTUNITY}>
                <Trans>Scan Opportunity Task</Trans>
              </SelectItem>
            )}
          </SelectContent>
        </Select>
      </legend>
      <PracticePreferencesSwitch
        selectedTask={selectedTask}
        preferences={preferences as PreferencesResponse}
        preferencesUpdate={handleUpdatePreferences}
      />
      {selectedTask === 'FLUORIDE_DUE' && <FlourideTaskAgeInput />}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={(open) => setIsOpen(open)}>
      <DialogContent
        className="vai-max-w-4xl vai-max-h-[95vh] vai-overflow-y-auto"
        data-testid="preferences-dialog"
      >
        <DialogHeader>
          <DialogTitle>{t`Preferences`}</DialogTitle>
        </DialogHeader>
        {Object.values(preferences?.allowEditingOrgLevelPreferences || {}).some(
          (x) => Boolean(x),
        ) &&
          isDirector && (
            <>
              <PrefPaneTitle>
                <div className="vai-flex vai-flex-col vai-gap-1">
                  <VideaTypography variant="labelSmallStrong">
                    <Trans>Organization-level preferences</Trans>
                  </VideaTypography>
                  <VideaTypography
                    variant="labelExtrasmall"
                    color={severitySevere}
                  >
                    <Trans>
                      Note: These preferences will be applied to every practice
                      in your organization.
                    </Trans>
                  </VideaTypography>
                </div>
              </PrefPaneTitle>
              <PrefPane>
                {preferences?.allowEditingOrgLevelPreferences
                  ?.customPatientEducationTxCopy && (
                  <div className="pane-section">
                    <PatientEducationTxCopyDialog />
                  </div>
                )}
              </PrefPane>
            </>
          )}
        <PrefPaneTitle>
          <VideaTypography variant="labelSmallStrong">
            <Trans>Practice-level preferences</Trans>
          </VideaTypography>
        </PrefPaneTitle>
        <PrefPane>
          {!hidePracticePreferences && (
            <div className="pane-section">
              <RBLColorSettings />
              <ConfidenceDialog showPreferenceButton />
              {(!hideHuddle || preferences?.forceEnableRecommendations) && (
                <RecommendationPreference />
              )}
            </div>
          )}
          {!hidePracticePreferences && <hr className="vai-w-full" />}
          <div className="pane-section">
            <HQOffByDefault />
          </div>
          <hr className="vai-w-full" />
          {!isHQOffByDefault && (
            <div className="pane-section">
              <EnableHqOnThumbnails />
            </div>
          )}
          {!hideHuddle && !hidePracticePreferences && (
            <>
              <hr className="vai-w-full" />
              <div className="pane-section vai-mb-2">
                <SpotlightPreference onChange={handleUpdatePreferences} />
              </div>
            </>
          )}
          {!hidePracticePreferences && (
            <>
              <hr className="vai-w-full" />
              {!hideHuddle && huddlePrefs}
            </>
          )}
        </PrefPane>
        {preferences?.saveSegmentationViewEnabled && (
          <>
            <PrefPaneTitle>
              <VideaTypography variant="labelSmallStrong">
                <Trans>User-level preferences</Trans>
              </VideaTypography>
            </PrefPaneTitle>
            <PrefPane>
              {preferences?.saveSegmentationViewEnabled && (
                <div className="pane-section">
                  <SegmentationPreferenceSettingsToggle />
                </div>
              )}
            </PrefPane>
          </>
        )}
        <PrefPaneTitle>
          <VideaTypography variant="labelSmallStrong">
            <Trans>Device-level preferences</Trans>
          </VideaTypography>
        </PrefPaneTitle>
        <PrefPane>
          {showPrlDevtools && (
            <div className="pane-section">
              <PreferencesCustomizer />
            </div>
          )}
          <PreferenceScrollToCurrent />
        </PrefPane>
      </DialogContent>
    </Dialog>
  );
};
